import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ShoppingCart, Check, Star } from 'lucide-react';
import { type Product, describePromoPrice } from '../store/cartSlice';
import { OptimizedImage } from './ui';
import { useCartActions } from '../hooks/useCartActions';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCartFn, buyNow, isAdded } = useCartActions();

  const { price, originalPrice, percentOff, caption } = describePromoPrice(product);
  const discountPercent = percentOff ?? (originalPrice && originalPrice > price
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : null);

  const fmt = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 0 });
  const added = isAdded(product.id);

  return (
    <div className={`bg-white rounded-xl border overflow-hidden flex flex-col group hover:shadow-md transition-all duration-200 ${product.isFeatured ? 'border-amber-300 hover:border-amber-400' : 'border-slate-200 hover:border-slate-300'}`}>

      {/* Image + Info → navega al detalle */}
      <Link to={`/producto/${product.id}`} className="flex flex-col flex-1">
        {/* Image */}
        <div className="relative aspect-square bg-slate-50 overflow-hidden">
          <OptimizedImage
            src={product.image}
            alt={product.name}
            displayWidth={300}
            widths={[200, 400]}
            quality={75}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-[1.04] ${
              product.imageSecondary ? 'group-hover:opacity-0' : ''
            }`}
          />

          {product.imageSecondary && (
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <OptimizedImage
                src={product.imageSecondary}
                alt={product.name}
                displayWidth={300}
                widths={[200, 400]}
                quality={75}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
              />
            </div>
          )}

          {product.promotion ? (
            <span className="absolute top-2 left-2 bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-md shadow-sm uppercase tracking-wider">
              {product.promotion.label}
            </span>
          ) : product.badge ? (
            <span className="absolute top-2 left-2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm">
              {product.badge}
            </span>
          ) : null}
          {discountPercent && (
            <span className="absolute top-2 right-2 bg-rose-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm">
              -{discountPercent}%
            </span>
          )}
          {product.isFeatured && (
            <span className="absolute bottom-2 right-2 flex items-center gap-0.5 bg-amber-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow-sm">
              <Star className="w-2.5 h-2.5 fill-current" />
              Destacado
            </span>
          )}
        </div>

        {/* Info */}
        <div className="p-2.5 flex flex-col gap-1 flex-1">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold leading-none">
            {product.category}
          </p>
          <h4 className="text-[12px] sm:text-[13px] text-slate-800 font-medium leading-snug line-clamp-2">
            {product.name}
          </h4>
          <div className="mt-auto pt-2">
            <p className="text-[15px] sm:text-base font-bold text-slate-900 leading-none">
              ${fmt(price)}
            </p>
            {originalPrice && originalPrice > price ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] text-slate-400 line-through">
                  ${fmt(originalPrice)}
                </span>
                {discountPercent && (
                  <span className="text-[10px] text-emerald-600 font-bold">
                    {discountPercent}% OFF
                  </span>
                )}
              </div>
            ) : caption ? (
              <p className="text-[10px] text-emerald-600 font-bold mt-0.5 leading-tight">{caption}</p>
            ) : product.promotion ? (
              <p className="text-[10px] text-emerald-600 font-bold mt-0.5 flex items-center gap-1">
                <span className="truncate">{product.promotion.name}</span>
              </p>
            ) : null}
          </div>
        </div>
      </Link>

      {/* Action buttons */}
      <div className="px-2.5 pb-2.5 flex gap-1.5">
        <button
          onClick={() => addToCartFn(product)}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${
            added
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
          }`}
        >
          {added ? (
            <><Check className="w-3 h-3" /> Agregado</>
          ) : (
            <><ShoppingCart className="w-3 h-3" /> Agregar</>
          )}
        </button>

        <button
          onClick={() => buyNow(product)}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold border bg-blue-600 border-blue-600 text-white hover:bg-blue-500 transition-all"
        >
          <ShoppingBag className="w-3 h-3" />
          Comprar
        </button>
      </div>
    </div>
  );
};
